/**
 * The collision volumes carried by the fighter rig.
 *
 * Two sets, because they answer different questions:
 *
 *   HITBOXES   what a fighter strikes *with* — the fists and the feet. A move
 *              names one of these, and it is dangerous only inside that move's
 *              active window.
 *   HURTBOXES  what a fighter can be struck *on*. Always live, and each one
 *              prices the blow differently: a head shot and a thigh shot are
 *              not the same hit even from the same punch.
 *
 * `bone` is matched by normalised name, so these survive the Mixamo namespace
 * mess that `boneNames.js` cleans up. `offset` is in the bone's local space and
 * rides it, which is why a forearm volume swings with the forearm.
 *
 * The offsets follow the same convention `scripts/rig-spec.mjs` used to build
 * the mannequin's limb boxes — a volume with no child bone is centred half a
 * thickness up its own +Y — so the debug spheres sit on the geometry the eye
 * sees rather than floating at the joints.
 */

/** Offensive volumes. `[name, bone, radius, offset]`. */
export const HITBOXES = Object.freeze([
  { name: 'rightHand', bone: 'RightHand', radius: 0.1, offset: [0, 0.04, 0] },
  { name: 'leftHand', bone: 'LeftHand', radius: 0.1, offset: [0, 0.04, 0] },
  { name: 'rightFoot', bone: 'RightFoot', radius: 0.11, offset: [0, 0.05, 0] },
  { name: 'leftFoot', bone: 'LeftFoot', radius: 0.11, offset: [0, 0.05, 0] },
]);

/**
 * Defensive volumes.
 *
 * `damageScale` is how much of the blow the body actually absorbs there, and
 * `consciousnessMultiplier` is what the health model charges per point of it.
 * The head is worth 4x consciousness at full damage; a raised forearm bleeds
 * most of the blow away and barely rings the bell, which is what makes a
 * glancing hit on the arms read as a graze rather than as a clean shot.
 */
export const HURTBOXES = Object.freeze([
  {
    name: 'head',
    bone: 'Head',
    radius: 0.16,
    offset: [0, 0.09, 0],
    damageScale: 1,
    consciousnessMultiplier: 4,
  },
  {
    name: 'chest',
    bone: 'Spine2',
    radius: 0.25,
    offset: [0, -0.02, 0],
    damageScale: 1,
    consciousnessMultiplier: 1.5,
  },
  {
    name: 'belly',
    bone: 'Spine',
    radius: 0.24,
    offset: [0, 0.03, 0],
    damageScale: 1,
    consciousnessMultiplier: 1.25,
  },
  {
    name: 'hips',
    bone: 'Hips',
    radius: 0.21,
    offset: [0, 0, 0],
    damageScale: 0.85,
    consciousnessMultiplier: 1,
  },
  {
    name: 'rightArm',
    bone: 'RightForeArm',
    radius: 0.13,
    offset: [0, 0, 0],
    damageScale: 0.35,
    consciousnessMultiplier: 0.7,
  },
  {
    name: 'leftArm',
    bone: 'LeftForeArm',
    radius: 0.13,
    offset: [0, 0, 0],
    damageScale: 0.35,
    consciousnessMultiplier: 0.7,
  },
  {
    name: 'rightLeg',
    bone: 'RightUpLeg',
    radius: 0.15,
    offset: [0, -0.21, 0],
    damageScale: 0.5,
    consciousnessMultiplier: 0.5,
  },
  {
    name: 'leftLeg',
    bone: 'LeftUpLeg',
    radius: 0.15,
    offset: [0, -0.21, 0],
    damageScale: 0.5,
    consciousnessMultiplier: 0.5,
  },
]);

/**
 * What each hurtbox covers on a real body, for `hurtFit.js`: the skin it
 * answers for, as parts — each a set of bones whose skin it fits, the bone the
 * sphere hangs on (so it moves with that skin), and how many spheres it takes.
 *
 * The mannequin's `HURTBOXES` give an arm a forearm and a leg a thigh. Fitted
 * that tightly, a fist on a guarding hand or an upper arm, or a kick on a shin,
 * touched skin and registered nothing — the oversized mannequin spheres had
 * caught some of those by accident. So an arm is upper arm, forearm and hand,
 * and a leg is thigh, shin and foot, priced as the arm or leg they belong to.
 * Spine1 has no sphere; its skin is the chest's. A thigh or shin is a cylinder,
 * and takes two spheres split along the bone.
 */
const limb = (side, upper, lower, end, split) => [
  { bones: [`${side}${upper}`], on: `${side}${upper}`, split },
  { bones: [`${side}${lower}`], on: `${side}${lower}`, split },
  { bones: [`${side}${end}`], on: `${side}${end}`, split: 1 },
];
export const HURT_PARTS = Object.freeze({
  head: [{ bones: ['Head', 'Neck'], on: 'Head', split: 1 }],
  chest: [{ bones: ['Spine2', 'Spine1', 'LeftShoulder', 'RightShoulder'], on: 'Spine2', split: 1 }],
  belly: [{ bones: ['Spine'], on: 'Spine', split: 1 }],
  hips: [{ bones: ['Hips'], on: 'Hips', split: 1 }],
  rightArm: limb('Right', 'Arm', 'ForeArm', 'Hand', 1),
  leftArm: limb('Left', 'Arm', 'ForeArm', 'Hand', 1),
  rightLeg: limb('Right', 'UpLeg', 'Leg', 'Foot', 2),
  leftLeg: limb('Left', 'UpLeg', 'Leg', 'Foot', 2),
});
